(ns manage.adset-decision-tree-page.layout
  (:require [cljs-bean.core :refer [->clj ->js]]
            [mate.core :as mc]))

(defn- walk-layout-tree [layout-tree prewalk-fn post-walk-fn]
  (-> layout-tree
      prewalk-fn
      (update :children (fn [children]
                          (->> children
                               (mapv (fn [child-node]
                                       (walk-layout-tree child-node prewalk-fn post-walk-fn))))))
      post-walk-fn))

(def ^:private vertical-space-between-siblings
  80)

(def ^:private horizontal-space-between-node-and-children
  80)

;; Computes the aggregated sizes from the tree leaves to the tree root
(defn- assoc-computed-sizes [tree]
  (let [{:keys [children]} tree
        node-width (-> tree :node-size :width)
        node-height (-> tree :node-size :height)
        children-width (transduce (map (comp :width :node+children-size)) max 0 children)
        children-height (+ (transduce (map (comp :height :node+children-size)) + 0 children)
                           (* (max 0 (dec (count children))) vertical-space-between-siblings))
        node+children-width (if (zero? children-width)
                              node-width
                              (+ node-width
                                 horizontal-space-between-node-and-children
                                 children-width))
        node+children-height (max node-height children-height)]
    (-> tree
        (assoc :children-size {:width  children-width
                               :height children-height}
               :node+children-size {:width node+children-width
                                    :height node+children-height}))))

(defn- assoc-children-position [tree]
  (-> tree
      (update :children
              (fn [children]
                (loop [children children
                       index 0
                       x (+ (-> tree :node-position :x)
                            (-> tree :node-size :width)
                            horizontal-space-between-node-and-children)
                       y (-> tree :node-position :y)]
                  (if (>= index (count children))
                    children
                    (recur (assoc-in children [index :node-position]
                                     {:x x
                                      :y y})
                           (inc index)
                           x
                           (+ y
                              (get-in children [index :node+children-size :height])
                              vertical-space-between-siblings))))))))

;; Returns a new reactflow-nodes with the new positions, and a list of which node-id was re-positioned
(defn re-layout-nodes [{:keys [node-id->node-data reactflow-nodes]}]
  (when (seq reactflow-nodes)
    (let [;; The starting node id
          root-node-id (->> (vals node-id->node-data)
                            (filter (comp #{:decision-tree-node/root} :type))
                            first
                            :id)

          ;; from the parent's id of a decision node to its own id
          parent-id->decision-node-id (->> (vals node-id->node-data)
                                           (filterv (comp #{:decision-node} :category))
                                           (mc/index-by :parent-id :id))

          ;; parent UI nodes (root node or decision nodes) -> ordered UI nodes (decision nodes)
          node-id->children-node-ids (->> (vals node-id->node-data)
                                          (keep (fn [node-data]
                                                  (let [node-id (:id node-data)]
                                                    (case (:category node-data)
                                                      :root-node (when-let [root-child (-> node-id parent-id->decision-node-id)]
                                                                   [node-id [root-child]])
                                                      :decision-node [node-id
                                                                      (->> (:outcome-node-ids node-data)
                                                                           (keep parent-id->decision-node-id)
                                                                           vec)]
                                                      :outcome-node nil))))
                                          (into {}))

          ;; The root node id and all the decision node ids
          node-ids (-> #{root-node-id}
                       (into cat (vals node-id->children-node-ids)))

          ;; We regroup all the data above
          node-id->layout-data (->> node-ids
                                    (mapv (fn [node-id]
                                            (let [^js js-node (-> node-id reactflow-nodes)]
                                              {:node-id node-id
                                               :children-node-ids (-> node-id node-id->children-node-ids)
                                               :node-position {:x (-> js-node .-position (some-> .-x) (or 0))
                                                               :y (-> js-node .-position (some-> .-y) (or 0))}
                                               :node-size {:width  (-> js-node .-measured (some-> .-width)  (or 0))
                                                           :height (-> js-node .-measured (some-> .-height) (or 0))}
                                               :hidden (.-hidden js-node)})))
                                    (mc/index-by :node-id))

          ;; Transform it into a tree data-structure
          build-tree (fn build-tree [node-id]
                       (let [layout-data (-> node-id node-id->layout-data)]
                         ;; Only add node to tree when it isn't hidden
                         (when-not (:hidden layout-data)
                           (-> layout-data
                               (dissoc :children-node-ids)
                               (assoc :children (->> (:children-node-ids layout-data)
                                                     (keep build-tree)
                                                     vec))))))
          layout-tree (build-tree root-node-id)

          ;; Compute the aggregated sizes, from tree leaves to tree root
          tree-with-sizes (walk-layout-tree layout-tree
                                            identity
                                            assoc-computed-sizes)

          ;; Compute the absolute positions, from tree root to tree leaves
          tree-with-positions (walk-layout-tree tree-with-sizes
                                                assoc-children-position
                                                identity)]

      ;; Create a new reactflow-nodes with the new node positions inside
      (loop [reactflow-nodes reactflow-nodes
             node-ids-to-patch []
             layout-tree-nodes (tree-seq (constantly true) :children tree-with-positions)]
        (if (seq layout-tree-nodes)
          (let [[{:keys [node-id node-position]} & next-nodes] layout-tree-nodes]
            (recur (-> reactflow-nodes
                       (update node-id (fn [^js js-node]
                                         (-> js-node
                                             ->clj
                                             (assoc :position node-position)
                                             ->js))))
                   (conj node-ids-to-patch node-id)
                   next-nodes))
          {:reactflow-nodes reactflow-nodes
           :node-ids-to-patch node-ids-to-patch})))))
